/**
 * AMPD DCMS2 — mobile tab bar
 *
 * Bottom navigation bar with the tabs of a dataset edit form on phones (viewport
 * below 640 px), modeled on the bottom menus of mobile news sites: a row of
 * buttons (icon + label) at the bottom edge that slides in while the page is
 * scrolled and slides out after a moment of stillness. Tapping a button switches
 * the form tab. A tab with a validation error (dcms-tab-has-error on its link —
 * Parsley or a server-side error) gets a red marker, and a new error brings the
 * bar on screen by itself.
 *
 * It lives in the HOST document, like the Action Dock: the edit form sits in a
 * stretched dataset iframe whose height iframe-resizer measures, so a fixed bar
 * inside the iframe would neither stick to the bottom of the phone screen nor
 * leave the height measurement alone. The Tabs component of the form
 * (views/UI/Components/tailwind/Tabs/tabs.tpl, Tabs::$mobileBar) registers its
 * tabs here through window.top.DCMSTabBar and keeps their state up to date;
 * a page that is not framed registers in its own window.
 *
 * Public API (reachable from same-origin iframes via window.top.DCMSTabBar):
 *
 *   DCMSTabBar.register(key, { doc, label, errorLabel, tabs, onSelect })
 *     doc        — document of the form (the entry is dropped once it unloads)
 *     label      — aria-label of the bar
 *     errorLabel — title of the error marker
 *     tabs       — [{ id, title, icon?, active, error }]
 *     onSelect   — fn(id): switches the tab in the form
 *   DCMSTabBar.update(key, tabs) — new state of the tabs (active tab, errors)
 *   DCMSTabBar.reveal(key)       — shows the bar for a while (e.g. new error)
 *   DCMSTabBar.setActions(doc, actions) — extra buttons after the tabs for the form
 *     of that document (e.g. "Text options"): [{ id, title, icon?, onClick }];
 *     [] removes them. May be called before or after the form registers its tabs.
 *   DCMSTabBar.scrollToElement(el) — scrolls the host page so that an element of
 *     the form (any same-origin frame depth) sits just below the top chrome
 *   DCMSTabBar.unregister(key)
 *
 * The most recently registered form owns the bar. When the Action Dock is the
 * bottom bar of the phone form, the tab bar slides in just above it.
 *
 * Opt-out for a whole page: window.__DCMS_NO_TAB_BAR = true.
 *
 * @copyright AMPD Sp. z o.o. / PTWP-ONLINE Sp. z o.o.
 * @author    Dominik Szmidt
 * @link      https://www.ampd.pl
 * @license   Commercial - usage requires a valid license
 * @see       /doc Documentation directory
 * @see       https://docs.ampd.pl Online documentation and examples
 */
(function () {
    'use strict';

    if (window.DCMSTabBar) { return; }

    // Stillness after which the bar slides out; an error keeps it twice as long.
    var HIDE_AFTER_MS = 1500;
    var NARROW_MQ_QUERY = '(max-width: 639px)';

    var entries = {}, order = [];
    // Extra action buttons per form document (setActions), applied to its entry on register too.
    var pendingActions = [];
    var bar = null, list = null, hideTimer = null, held = false, shown = false, offsetTimer = null;
    // The Action Dock settles into its phone bottom bar on its own timer (and may be collapsed,
    // moved or hidden meanwhile) — the offset is re-measured this often while the bar is shown.
    var OFFSET_SYNC_MS = 250;
    var narrowMq = window.matchMedia ? window.matchMedia(NARROW_MQ_QUERY) : null;
    var hostScrollBound = false;

    function isNarrow() { return !!(narrowMq && narrowMq.matches); }

    // The entry that owns the bar: the last registered one whose document is still loaded.
    function current() {
        for (var i = order.length - 1; i >= 0; i--) {
            var entry = entries[order[i]];
            var alive = false;
            try { alive = !!(entry && entry.doc && entry.doc.defaultView); } catch (e) { alive = false; }
            if (alive) { return entry; }
            remove(order[i]);
        }
        return null;
    }

    function actionsFor(doc) {
        for (var i = 0; i < pendingActions.length; i++) {
            if (pendingActions[i].doc === doc) { return pendingActions[i].actions; }
        }
        return [];
    }

    function remove(key) {
        delete entries[key];
        order = order.filter(function (k) { return k !== key; });
    }

    /* ---- DOM ------------------------------------------------------------ */

    function build() {
        if (bar) { return; }
        bar = document.createElement('nav');
        bar.className = 'dcms-tab-bar';
        bar.setAttribute('aria-hidden', 'true');
        list = document.createElement('div');
        list.className = 'dcms-tab-bar-list';
        bar.appendChild(list);
        // A finger on the bar keeps it on screen (scrolling the bar sideways included).
        var hold = function () { held = true; clearHideTimer(); };
        var release = function () { held = false; scheduleHide(HIDE_AFTER_MS); };
        bar.addEventListener('pointerdown', hold);
        bar.addEventListener('pointerup', release);
        bar.addEventListener('pointercancel', release);
        bar.addEventListener('pointerleave', release);
        bar.addEventListener('focusin', hold);
        bar.addEventListener('focusout', release);
        list.addEventListener('scroll', function () { if (shown) { scheduleHide(HIDE_AFTER_MS); } }, { passive: true });
        document.body.appendChild(bar);
    }

    function render(entry) {
        build();
        bar.setAttribute('aria-label', entry.label || '');
        var tabs = entry.tabs || [];
        var actions = entry.actions || [];
        // Rebuild only when the set of tabs / actions changed; state changes only toggle classes.
        var sig = tabs.map(function (tab) { return tab.id + '|' + tab.title + '|' + (tab.icon || ''); }).join(';') +
            '!' + actions.map(function (a) { return a.id + '|' + a.title + '|' + (a.icon || ''); }).join(';') + '#' + entry.key;
        if (list.getAttribute('data-sig') !== sig) {
            list.setAttribute('data-sig', sig);
            while (list.firstChild) { list.removeChild(list.firstChild); }
            tabs.forEach(function (tab) {
                var button = document.createElement('button');
                button.type = 'button';
                button.className = 'dcms-tab-bar-item';
                button.setAttribute('data-tab-bar-id', tab.id);
                if (tab.icon) {
                    var icon = document.createElement('i');
                    icon.className = 'msr dcms-tab-bar-icon';
                    icon.setAttribute('aria-hidden', 'true');
                    icon.textContent = tab.icon;
                    button.appendChild(icon);
                } else {
                    button.classList.add('dcms-tab-bar-item--text');
                }
                var label = document.createElement('span');
                label.className = 'dcms-tab-bar-label';
                label.textContent = tab.title;
                button.appendChild(label);
                var err = document.createElement('i');
                err.className = 'msr dcms-tab-bar-err';
                err.setAttribute('aria-hidden', 'true');
                err.textContent = 'error';
                button.appendChild(err);
                button.addEventListener('click', function () { select(tab.id); });
                list.appendChild(button);
            });
            // Extra actions after the tabs — same look, no active / error state
            actions.forEach(function (action) {
                var button = document.createElement('button');
                button.type = 'button';
                button.className = 'dcms-tab-bar-item dcms-tab-bar-action';
                button.setAttribute('data-tab-bar-action', action.id || '');
                if (action.icon) {
                    var icon = document.createElement('i');
                    icon.className = 'msr dcms-tab-bar-icon';
                    icon.setAttribute('aria-hidden', 'true');
                    icon.textContent = action.icon;
                    button.appendChild(icon);
                } else {
                    button.classList.add('dcms-tab-bar-item--text');
                }
                var label = document.createElement('span');
                label.className = 'dcms-tab-bar-label';
                label.textContent = action.title || '';
                button.appendChild(label);
                button.addEventListener('click', function () {
                    try { if (typeof action.onClick === 'function') { action.onClick(); } } catch (e) { /* the form may be leaving */ }
                    scheduleHide(HIDE_AFTER_MS);
                });
                list.appendChild(button);
            });
        }
        var activeButton = null;
        tabs.forEach(function (tab, i) {
            var button = list.children[i];
            if (!button) { return; }
            button.classList.toggle('is-active', !!tab.active);
            button.classList.toggle('has-error', !!tab.error);
            if (tab.active) {
                button.setAttribute('aria-current', 'page');
                activeButton = button;
            } else {
                button.removeAttribute('aria-current');
            }
            var title = tab.error && entry.errorLabel ? tab.title + ' — ' + entry.errorLabel : '';
            if (title) { button.title = title; } else { button.removeAttribute('title'); }
        });
        revealButton(activeButton);
    }

    // Scrolls the bar (never the page) so that the button is fully visible.
    function revealButton(button) {
        if (!button || list.scrollWidth <= list.clientWidth) { return; }
        var listRect = list.getBoundingClientRect();
        var rect = button.getBoundingClientRect();
        if (rect.left < listRect.left) {
            list.scrollLeft -= listRect.left - rect.left;
        } else if (rect.right > listRect.right) {
            list.scrollLeft += rect.right - listRect.right;
        }
    }

    function select(id) {
        var entry = current();
        if (!entry || typeof entry.onSelect !== 'function') { return; }
        try { entry.onSelect(id); } catch (e) { /* the form may be leaving */ }
        scheduleHide(HIDE_AFTER_MS);
    }

    /* ---- visibility ------------------------------------------------------ */

    // Space taken at the bottom by the Action Dock when it is the bottom bar.
    function bottomOffset() {
        var dock = document.querySelector('.dcms-action-dock.dcms-dock-bottom.is-visible');
        if (!dock) { return 0; }
        var rect = dock.getBoundingClientRect();
        return Math.max(0, Math.round(window.innerHeight - rect.top));
    }

    function show() {
        var entry = current();
        if (!entry || !isNarrow() || window.__DCMS_NO_TAB_BAR) { hide(); return; }
        render(entry);
        syncOffset();
        if (!shown) {
            shown = true;
            bar.classList.add('is-visible');
            bar.setAttribute('aria-hidden', 'false');
            offsetTimer = window.setInterval(syncOffset, OFFSET_SYNC_MS);
        }
    }

    function syncOffset() {
        var value = bottomOffset() + 'px';
        if (bar.style.getPropertyValue('--dcms-tab-bar-offset') !== value) {
            bar.style.setProperty('--dcms-tab-bar-offset', value);
        }
    }

    function hide() {
        clearHideTimer();
        if (!bar || !shown) { return; }
        shown = false;
        held = false;
        if (offsetTimer) {
            window.clearInterval(offsetTimer);
            offsetTimer = null;
        }
        bar.classList.remove('is-visible');
        bar.setAttribute('aria-hidden', 'true');
        // A hidden bar must not keep the keyboard focus.
        if (bar.contains(document.activeElement)) { document.activeElement.blur(); }
    }

    function clearHideTimer() {
        if (hideTimer) {
            window.clearTimeout(hideTimer);
            hideTimer = null;
        }
    }

    function scheduleHide(ms) {
        clearHideTimer();
        if (held) { return; }
        hideTimer = window.setTimeout(hide, ms);
    }

    function onMove() {
        if (!current()) { return; }
        show();
        if (shown) { scheduleHide(HIDE_AFTER_MS); }
    }

    function bindHostScroll() {
        if (hostScrollBound) { return; }
        hostScrollBound = true;
        window.addEventListener('scroll', onMove, { passive: true });
    }

    /* ---- scrolling the page to a form element ---------------------------- */

    // Bottom edge of the fixed/sticky chrome at the top of the host page (top bar of
    // the Action Dock, a sticky header), so a scrolled-to element is not hidden under it.
    function topChrome() {
        var bottom = 0;
        var candidates = document.querySelectorAll('.dcms-action-dock.dcms-dock-top.is-visible, header.topbar');
        [].forEach.call(candidates, function (el) {
            var position = window.getComputedStyle(el).position;
            if (position !== 'fixed' && position !== 'sticky') { return; }
            var rect = el.getBoundingClientRect();
            if (rect.top <= 1 && rect.bottom > bottom) { bottom = rect.bottom; }
        });
        return bottom;
    }

    function scrollToElement(el) {
        if (!el || !el.getBoundingClientRect) { return; }
        var y = el.getBoundingClientRect().top;
        var win = el.ownerDocument && el.ownerDocument.defaultView;
        try {
            while (win && win !== window) {
                var frame = win.frameElement;
                if (!frame) { return; }
                y += frame.getBoundingClientRect().top;
                win = win.parent;
            }
        } catch (e) { return; }
        var chrome = topChrome();
        // Already in the upper part of the screen — leave the page where it is.
        if (y >= chrome && y <= window.innerHeight * 0.4) { return; }
        window.scrollTo({ top: Math.max(0, window.scrollY + y - chrome - 8), behavior: 'smooth' });
    }

    /* ---- API ------------------------------------------------------------- */

    window.DCMSTabBar = {
        supports: { scrollToElement: true, actions: true },
        register: function (key, cfg) {
            if (!key || !cfg || !cfg.doc) { return; }
            remove(key);
            entries[key] = {
                key: key,
                doc: cfg.doc,
                label: cfg.label || '',
                errorLabel: cfg.errorLabel || '',
                tabs: cfg.tabs || [],
                onSelect: cfg.onSelect,
                actions: actionsFor(cfg.doc)
            };
            order.push(key);
            bindHostScroll();
            // A form that is not framed, or a frame that scrolls on its own
            try {
                var formWin = cfg.doc.defaultView;
                if (formWin && formWin !== window) { formWin.addEventListener('scroll', onMove, { passive: true }); }
            } catch (e) { /* cross-origin — the host scroll is enough */ }
            if (shown) { show(); }
        },
        update: function (key, tabs) {
            var entry = entries[key];
            if (!entry) { return; }
            entry.tabs = tabs || [];
            if (shown && current() === entry) { render(entry); }
        },
        reveal: function (key) {
            if (!entries[key] || current() !== entries[key]) { return; }
            show();
            if (shown) { scheduleHide(HIDE_AFTER_MS * 2); }
        },
        setActions: function (doc, actions) {
            if (!doc) { return; }
            pendingActions = pendingActions.filter(function (p) {
                var alive = false;
                try { alive = !!(p.doc && p.doc.defaultView); } catch (e) { alive = false; }
                return alive && p.doc !== doc;
            });
            var list = Array.isArray(actions) ? actions.slice() : [];
            if (list.length) { pendingActions.push({ doc: doc, actions: list }); }
            Object.keys(entries).forEach(function (key) {
                if (entries[key].doc === doc) { entries[key].actions = list; }
            });
            var entry = current();
            if (shown && entry && entry.doc === doc) { render(entry); }
        },
        scrollToElement: scrollToElement,
        unregister: function (key) {
            remove(key);
            if (!current()) { hide(); } else if (shown) { show(); }
        }
    };

    if (narrowMq) {
        var onChange = function () { if (!isNarrow()) { hide(); } };
        if (narrowMq.addEventListener) { narrowMq.addEventListener('change', onChange); }
        else if (narrowMq.addListener) { narrowMq.addListener(onChange); }
    }
})();
